import * as actionTypes from './actionTypes';
import { EventSectionActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { EventSection } from '../../domain/EventSection';
import { ListResults } from '../../common/List/List';

export type EventSectionStateType = {
  createEventSectionLoading: boolean;
  createdEventSection: EventSection | null;
  createEventSectionError: HttpError;

  eventSections: ListResults<EventSection> | null;
  eventSectionsLoading: boolean;
  eventSectionsError: HttpError;
  eventSectionsUpdateNeeded: boolean;

  eventSectionUpdateLoading: boolean;
  eventSectionUpdateSuccess: boolean;
  eventSectionUpdateError: HttpError;

  eventSectionDeleteLoading: boolean;
  eventSectionDeleteError: HttpError;
  eventSectionDeleteSuccess: boolean;

  assignEventToSectionLoading: boolean;
  assignEventToSectionSuccess: boolean;
  assignEventToSectionError: HttpError;
};

export type EventSectionActionType = EventSectionStateType & {
  type: EventSectionActionTypes;
  eventId: number;
};

export const initialState: EventSectionStateType = {
  createEventSectionLoading: false,
  createdEventSection: null,
  createEventSectionError: null,

  eventSections: null,
  eventSectionsLoading: false,
  eventSectionsError: null,
  eventSectionsUpdateNeeded: false,

  eventSectionUpdateLoading: false,
  eventSectionUpdateSuccess: false,
  eventSectionUpdateError: null,

  eventSectionDeleteLoading: false,
  eventSectionDeleteError: null,
  eventSectionDeleteSuccess: false,

  assignEventToSectionLoading: false,
  assignEventToSectionSuccess: false,
  assignEventToSectionError: null,
};

const createEventSectionStart = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  createEventSectionLoading: true,
});

const createEventSectionSuccess = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  createEventSectionLoading: false,
  createEventSectionError: null,
  createdEventSection: action.createdEventSection,
  eventSectionsUpdateNeeded: true,
});

const createEventSectionFail = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  createEventSectionLoading: false,
  createEventSectionError: action.createEventSectionError,
});

const fetchEventSectionsStart = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  eventSectionsLoading: true,
});

const fetchEventSectionsSuccess = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  eventSections: action.eventSections,
  eventSectionsLoading: false,
  eventSectionsError: null,
  eventSectionsUpdateNeeded: false,
  eventSectionDeleteSuccess: false,
});

const fetchEventSectionsFail = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  eventSectionsLoading: false,
  eventSectionsError: action.eventSectionsError,
});

const updateEventSectionStart = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  eventSectionUpdateLoading: true,
});

const updateEventSectionSuccess = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  eventSectionUpdateLoading: false,
  eventSectionUpdateError: null,
  eventSectionUpdateSuccess: true,
  eventSectionsUpdateNeeded: true,
});

const updateEventSectionFail = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  eventSectionUpdateLoading: false,
  eventSectionUpdateError: action.eventSectionUpdateError,
});

const deleteEventSectionStart = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  eventSectionDeleteLoading: true,
});

const deleteEventSectionSuccess = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  eventSectionDeleteLoading: false,
  eventSectionDeleteError: null,
  eventSectionDeleteSuccess: true,
  eventSectionsUpdateNeeded: true,
});

const deleteEventSectionFail = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  eventSectionDeleteLoading: false,
  eventSectionDeleteError: action.eventSectionDeleteError,
});

const assignEventToSectionStart = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  assignEventToSectionLoading: true,
});

const assignEventToSectionSuccess = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  assignEventToSectionLoading: false,
  assignEventToSectionError: null,
  assignEventToSectionSuccess: true,
  eventSectionsUpdateNeeded: true,
});

const assignEventToSectionFail = (
  state: EventSectionStateType,
  action: EventSectionActionType,
): EventSectionStateType => ({
  ...state,
  assignEventToSectionLoading: false,
  assignEventToSectionError: action.assignEventToSectionError,
});

const resetAssignEventToSection = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  assignEventToSectionLoading: false,
  assignEventToSectionError: null,
  assignEventToSectionSuccess: false,
});

const resetCreateEventSection = (
  state: EventSectionStateType,
): EventSectionStateType => ({
  ...state,
  createEventSectionLoading: false,
  createEventSectionError: null,
  createdEventSection: null,
});

const resetStore = (): EventSectionStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: EventSectionActionType) => {
  switch (action.type) {
    case actionTypes.CREATE_EVENT_SECTION_START:
      return createEventSectionStart(state);
    case actionTypes.CREATE_EVENT_SECTION_SUCCESS:
      return createEventSectionSuccess(state, action);
    case actionTypes.CREATE_EVENT_SECTION_FAIL:
      return createEventSectionFail(state, action);
    case actionTypes.FETCH_EVENT_SECTIONS_START:
      return fetchEventSectionsStart(state);
    case actionTypes.FETCH_EVENT_SECTIONS_SUCCESS:
      return fetchEventSectionsSuccess(state, action);
    case actionTypes.FETCH_EVENT_SECTIONS_FAIL:
      return fetchEventSectionsFail(state, action);
    case actionTypes.UPDATE_EVENT_SECTION_START:
      return updateEventSectionStart(state);
    case actionTypes.UPDATE_EVENT_SECTION_SUCCESS:
      return updateEventSectionSuccess(state, action);
    case actionTypes.UPDATE_EVENT_SECTION_FAIL:
      return updateEventSectionFail(state, action);
    case actionTypes.DELETE_EVENT_SECTION_START:
      return deleteEventSectionStart(state);
    case actionTypes.DELETE_EVENT_SECTION_SUCCESS:
      return deleteEventSectionSuccess(state, action);
    case actionTypes.DELETE_EVENT_SECTION_FAIL:
      return deleteEventSectionFail(state, action);
    case actionTypes.ASSIGN_EVENT_TO_SECTION_START:
      return assignEventToSectionStart(state);
    case actionTypes.ASSIGN_EVENT_TO_SECTION_SUCCESS:
      return assignEventToSectionSuccess(state, action);
    case actionTypes.ASSIGN_EVENT_TO_SECTION_FAIL:
      return assignEventToSectionFail(state, action);
    case actionTypes.RESET_ASSIGN_EVENT_TO_SECTION:
      return resetAssignEventToSection(state);
    case actionTypes.RESET_CREATE_EVENT_SECTION:
      return resetCreateEventSection(state);
    case actionTypes.RESET_EVENT_SECTION_STORE:
      return resetStore();
    default:
      return state;
  }
};

export default reducer;
